import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import MuiModalFrame from "./MuiModalFrame";
import PermissionAreas from "../components/PermissionAreas";
import { useEffect, useState } from "react";
import {
  CANCEL_IMPERSONATE_USER,
  GET_USER_PERMISSIONS,
  IMPERSONATE_USER,
} from "../constants/apiUrls";
import { apiGetAuth, serverMessage } from "../calls/apiCalls";
import { authClient } from "../calls/http";
import { permissionsCodes } from "../constants/users";
import { setTokens } from "../utils/authStorage";
import { useSnackbar } from "../contexts/SnackbarContext";

function permissionPayload(source) {
  const flags = {};
  Object.values(permissionsCodes).forEach((name) => {
    flags[name] = source?.[name] === true;
  });
  flags.r5 = source?.r5 === true;
  return flags;
}

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string") return data.message;
  return fallback;
}

export default function ImpersonationModal({
  open,
  user,
  users,
  permissions,
  onClose,
}) {
  const { showSnackbar } = useSnackbar();
  const [userUUID, setUserUUID] = useState(user?.uuid || "");
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [impersonationPermissions, setImpersonationPermissions] =
    useState(permissions);

  const viewingSelf = !userUUID || userUUID === user?.uuid;

  useEffect(() => {
    if (open) {
      setUserUUID(user?.uuid || "");
    }
  }, [open, user?.uuid]);

  useEffect(() => {
    let active = true;

    const loadPermissions = async () => {
      if (!userUUID || userUUID === user?.uuid) {
        setImpersonationPermissions(permissions);
        setLoadingPermissions(false);
        return;
      }

      setLoadingPermissions(true);
      const response = await apiGetAuth(`${GET_USER_PERMISSIONS}/${userUUID}`);
      if (!active) return;

      const loaded =
        response && typeof response === "object" && "u1" in response;
      if (!loaded) {
        showSnackbar(
          serverMessage(response, "Could not load this user's access"),
        );
        setLoadingPermissions(false);
        return;
      }

      const ceiling = permissions?.companyPermissions;
      const flags =
        response?.user?.role === "SuperUser" && ceiling
          ? permissionPayload(ceiling)
          : permissionPayload(response);

      setImpersonationPermissions({
        ...response,
        ...flags,
        companyPermissions: ceiling,
      });
      setLoadingPermissions(false);
    };

    loadPermissions();
    return () => {
      active = false;
    };
  }, [userUUID, user?.uuid, permissions, showSnackbar]);

  const handleImpersonateClick = async () => {
    if (!userUUID) {
      showSnackbar("Choose a user to impersonate.");
      return;
    }

    setLoading(true);
    try {
      const response = await authClient.post(IMPERSONATE_USER, {
        userUUID,
        permissions: permissionPayload(impersonationPermissions),
      });
      if (!response?.data?.accessToken || !response?.data?.refreshToken) {
        showSnackbar("Could not impersonate this user");
        setLoading(false);
        return;
      }

      setTokens({
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      });
      window.location.reload();
    } catch (error) {
      showSnackbar(errorMessage(error, "Could not impersonate this user"));
      setLoading(false);
    }
  };

  const handleCancelImpersonationClick = async () => {
    setLoading(true);
    try {
      const response = await authClient.get(CANCEL_IMPERSONATE_USER);
      if (!response?.data?.accessToken || !response?.data?.refreshToken) {
        showSnackbar("Could not stop impersonating");
        setLoading(false);
        return;
      }

      setTokens({
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      });
      window.location.reload();
    } catch (error) {
      showSnackbar(errorMessage(error, "Could not stop impersonating"));
      setLoading(false);
    }
  };

  const choices = (users || []).filter(
    (person) => person.active !== false && person.uuid !== user?.uuid,
  );

  return (
    <MuiModalFrame open={open} onClose={onClose} title="Impersonation">
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Impersonating a user shows the locations, pages, forms, and assets they
        can see. The access below applies to this session only and is not saved
        to their account.
      </Typography>
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel id="impersonate-user-label">Impersonate User</InputLabel>
        <Select
          labelId="impersonate-user-label"
          id="impersonate-user"
          label="Impersonate User"
          value={userUUID}
          onChange={(e) => setUserUUID(e.target.value)}
        >
          {user?.uuid && (
            <MenuItem value={user.uuid}>
              {user.lastName}, {user.firstName} (current session)
            </MenuItem>
          )}
          {choices.map((person) => (
            <MenuItem key={person.uuid} value={person.uuid}>
              {person.lastName}, {person.firstName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
        Level of Access
      </Typography>
      <Box sx={{ maxHeight: 480, overflow: "auto" }}>
        {loadingPermissions ? (
          <Box
            sx={{
              width: "100%",
              minHeight: 160,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <PermissionAreas
            mode="grant"
            permissions={impersonationPermissions}
            companyPermissions={permissions?.companyPermissions}
            onChange={setImpersonationPermissions}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
        <Button
          sx={{ minWidth: 160 }}
          variant="contained"
          disabled={loading || loadingPermissions || !userUUID}
          onClick={handleImpersonateClick}
        >
          {loading ? (
            <CircularProgress size="24px" />
          ) : viewingSelf ? (
            "Preview access"
          ) : (
            "Impersonate"
          )}
        </Button>
        <Button
          variant="outlined"
          disabled={loading}
          color="error"
          onClick={handleCancelImpersonationClick}
        >
          Stop impersonating
        </Button>
        <Button
          variant="outlined"
          disabled={loading}
          onClick={onClose}
        >
          Close
        </Button>
      </Box>
    </MuiModalFrame>
  );
}
